import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { deleteNonSessionPayment, getCoachPayment, getOngoingPayments, getTotalSumFromLifetime } from '@/lib/api-calls';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FromOptions } from './from-options';
import { useToast } from '@/components/ui/hooks/use-toast';
import { airServerErrorFrom } from '@/lib/error-handling';

type DeletePaymentProps = {
  id: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  from: FromOptions;
};

export const DeletePayment = (props: DeletePaymentProps) => {
  const { id, isOpen, onOpenChange, from } = props;
  const { toast } = useToast();

  const queryClient = useQueryClient();

  const { mutateAsync } = useMutation({
    mutationFn: deleteNonSessionPayment,
  });

  const onDelete = async () => {
    onOpenChange(false);
    try {
      await mutateAsync({ id });
      if (from === FromOptions.teacherPay) {
        queryClient.invalidateQueries(getOngoingPayments());
      }
      if (from === FromOptions.payments) {
        queryClient.invalidateQueries(getCoachPayment());
      }
      queryClient.invalidateQueries(getTotalSumFromLifetime());
      toast({ title: 'Payment deleted', variant: 'default' });
    } catch (error) {
      toast({
        variant: 'destructive',
        description: airServerErrorFrom(error)?.userFacingMessage ?? 'Failed to delete payment.',
      });
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete payment</DialogTitle>
          </DialogHeader>
          <DialogDescription>Are you sure you want to delete this payment?</DialogDescription>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="secondary">Cancel</Button>
            </DialogClose>
            <Button variant="destructive" onClick={onDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
